import { buildingDetailsBreadcrumb } from "../buildings/buildings-breadcrumbs";
import { clientDetailsBreadcrumb } from "../clients/client-breadcrumb";

/**
 * Interface for the parameters used in breadcrumb functions.
 * 
 * @interface IParams
 * @property {Object} client - Client information
 * @property {string} [client.name] - Client name
 * @property {number} client.id - Client ID
 * @property {Object} building - Building information
 * @property {string} [building.name] - Building name
 * @property {number} building.id - Building ID
 * @property {Object} [plant] - Plant information
 * @property {string} [plant.name] - Plant name
 * @property {number} [plant.id] - Plant ID
 */
export interface IParams {
  client: { name?: string; id: number };
  building: { name?: string; id: number };
  plant?: { name?: string; id: number };
}

/**
 * Generates breadcrumb navigation for plant details page.
 * 
 * @param {IParams} props - Parameters containing client, building, and plant information
 * @returns {Array<Object>} Array of breadcrumb items with title and path
 * 
 * @example
 * ```tsx
 * const breadcrumbs = plantDetailsBreadcrumb({
 *   client: { name: "Client Name", id: 1 },
 *   building: { name: "Building Name", id: 2 },
 *   plant: { name: "Plant Name", id: 3 }
 * });
 * ```
 */
export const plantDetailsBreadcrumb = (props: IParams) => {
  const { client, building, plant } = props;

  const buildingDetails = buildingDetailsBreadcrumb({
    client: { name: client?.name, id: client?.id },
    building: { name: building?.name, id: building?.id },
  });

  const plantDetails = [
    ...buildingDetails,
    {
      title: "Anlage",
      path: `/clients/${client.id}/buildings/${building?.id}`,
    },
    {
      title: plant?.name ? plant?.name : "Loading...",
      path: `/clients/${client.id}/buildings/${building?.id}/plants/${plant?.id}`,
      disabledPath: true,
    },
  ];

  return plantDetails.filter((item) => item.title !== "");
};

/**
 * Generates breadcrumb navigation for creating a new plant.
 * 
 * @param {IParams} props - Parameters containing client and building information
 * @returns {Array<Object>} Array of breadcrumb items with title and path
 * 
 * @example
 * ```tsx
 * const breadcrumbs = createPlantsBreadcrumb({
 *   client: { name: "Client Name", id: 1 },
 *   building: { name: "Building Name", id: 2 }
 * });
 * ```
 */
export const createPlantsBreadcrumb = (props: IParams) => {
  const { client, building } = props;

  const clientDetails = clientDetailsBreadcrumb({
    client: { name: client?.name, id: client?.id },
  });

  const buildingDetails = [
    ...clientDetails,
    { title: "Gebäude", path: `/clients/${client.id}` },
    {
      title: building?.name ? building?.name : "Loading...",
      path: `/clients/${client.id}/buildings/${building?.id}`,
    },
    {
      title: "Anlage",
      path: `/clients/${client.id}/buildings/${building?.id}`,
      disabledPath: true,
    },
  ];

  return buildingDetails.filter((item) => item.title !== "");
};
